/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * The footer component of SubscriberMobileTable
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import { FC, useCallback, useMemo } from 'react';

import filter from 'lodash/filter';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getIsFastActionPage, getIsFastBasketPage } from '@/main/components/Table/utils/tableUtils';
import { RoutePath } from '@/main/routes/constants/routesConstants';

import {
    IUseTableFooterButtons,
    TableFooterButtonContainerStyled,
    TableFooterContainerStyled,
    TableFooterItem,
    TableFooterLeftContainerStyled,
    TableFooterRightContainerStyled,
    TableFooterShowMenu,
    useTableFooterButtons,
    useTableFooterDisabled,
} from '../../../components/common/TableFooter';
import { TableFooterOpenHandlerStyled } from '../../../components/common/TableFooter/styled/TableFooterStyled';
import { TableFooterNameType } from '../../../components/Table/types/tableTypes';
import {
    EGTMBasketSectionType,
    EGTMBasketValueType,
} from '../../../modules/analytics/GoogleTagManager/GTMOperations/GTMBasketOperations';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageSubscriberMobile,
} from '../../../modules/internationalization/intSelectors';
import { ITableButton } from '../../../modules/table';
import { useBasketModalOpen } from '../../../modules/table/components/Basket/Modal/useBasketModalOpen';
import {
    tableBasketCountSelector,
    tableBasketParamsSelector,
    tableButtonsModeSelector,
    tableDataSelector,
} from '../../../modules/table/selectors';
import { FastInquiryCreateButtons } from '../../FastInquiryCreate/FastInquiryCreateButtons';

export interface ISubscriberMobileTableFooterProps {
    isOpen: boolean;
    setOpen: (isOpen: boolean) => void;
    buttons: ITableButton[];
    buttonsEnabled: { [buttonName: string]: boolean };
}

export const SubscriberMobileTableFooter = (props: ISubscriberMobileTableFooterProps) => {

    const {
        isOpen,
        setOpen, buttons, buttonsEnabled,
    } = props;

    const location = useLocation();

    const { tableFooter: tTableFooter } = useSelector(makeSelectTranslationPageSubscriberMobile());
    const { basket: tBasket } = useSelector(makeSelectTranslationCommon());

    const basketCount = useSelector(tableBasketCountSelector);
    const buttonsMode = useSelector(tableButtonsModeSelector);
    const basketParams = useSelector(tableBasketParamsSelector);
    const tableData = useSelector(tableDataSelector);
    
    const fastActionChechedItems = filter(tableData, (item) => !!item.fastActionChecked);
    const countFastActionChecked = fastActionChechedItems?.length ?? 0;
    const isOnlyOneSubscriberSelected = countFastActionChecked === 1 && !!fastActionChechedItems[0];
    const isFastActionPage = getIsFastActionPage();
    const isFastBasketPage = getIsFastBasketPage();

    const {
        accessLevel,
        additionalAccount,
        callDetails,
        corporate,
        expenses,
        inquiryCreate,
        labelEdit,
        limit,
        manageBlock,
        mobileReport,
        tariffChange,
    } = useTableFooterButtons(
        TableFooterNameType.subscriberMobile, 
        buttons, 
        isOnlyOneSubscriberSelected ? fastActionChechedItems[0].id : undefined,
    );

    const {
        isBtnDisabled,
        getFooterButtonTooltipMsg,
    } = useTableFooterDisabled(
        TableFooterNameType.subscriberMobile,
        /* TODO: remove props, use selector inside hook */
        buttons,
        buttonsMode,
        isFastBasketPage ? countFastActionChecked : basketCount,
        basketParams,
    );

     /* Basket */
    const { open: openBasketModal } = useBasketModalOpen({
        gtm: {
            sectionType: EGTMBasketSectionType.MOBILE,
            valueType: EGTMBasketValueType.NUMBER,
        },
    });

    const getTooltipMsg = useCallback((item: IUseTableFooterButtons): string =>
        getFooterButtonTooltipMsg(item.id) || item.tooltipMsg, [getFooterButtonTooltipMsg]);

    const renderBtn = useCallback((item: IUseTableFooterButtons) => buttonsEnabled[item.id] && (
        <TableFooterItem
            {...item}
            isDisabled={isBtnDisabled(item.id)}
            key={item.id}
            tooltipMsg={getTooltipMsg(item)}
        />
    ), [buttonsEnabled, isBtnDisabled, getTooltipMsg]);

    const basket = useMemo(() =>
        <>
            {!!basketCount && (
                    <TableFooterOpenHandlerStyled
                        style={{ left: 40 }}
                        onClick={() => openBasketModal(tTableFooter.basketTitle)}
                    >
                        {tBasket.checkedNum} {basketCount}
                    </TableFooterOpenHandlerStyled>
                )}
                <TableFooterShowMenu
                    gtmSectionType={EGTMBasketSectionType.MOBILE}
                    isOpen={isOpen}
                    onClick={() => setOpen(!isOpen)}
                />
        </>
        ,[basketCount, isOpen, openBasketModal, setOpen, tBasket.checkedNum, tTableFooter.basketTitle])

    const tooltipMsg = getTooltipMsg(inquiryCreate);

    const TableFooter: FC = () => {
        switch (location.pathname) {
            case RoutePath.fastInquiryCreate:
                return (
                    <TableFooterLeftContainerStyled className="table-footer__left-container">
                        <FastInquiryCreateButtons 
                            cancelButtonText={tTableFooter.cancel} 
                            inquiryCreate={inquiryCreate} 
                            isDisabled={!isOnlyOneSubscriberSelected}
                            tooltipMsg={tooltipMsg}
                        />
                    </TableFooterLeftContainerStyled>
                );
            default:
                return (
                    <>
                        <TableFooterLeftContainerStyled className="table-footer__left-container">
                            {
                                [manageBlock, limit, corporate, labelEdit, callDetails,
                                    tariffChange, expenses, inquiryCreate, additionalAccount].map(renderBtn)
                            }
                        </TableFooterLeftContainerStyled>

                        <TableFooterRightContainerStyled className="table-footer__right-container">
                            {[accessLevel, mobileReport].map(renderBtn)}
                        </TableFooterRightContainerStyled>
                    </>
                );
        }
    };

    return (
        <TableFooterContainerStyled className="table-footer subscriber-footer">
            {!isFastActionPage && basket}
            <TableFooterButtonContainerStyled>
                {isOpen && <TableFooter />}
            </TableFooterButtonContainerStyled>
        </TableFooterContainerStyled>

    );
};

export default SubscriberMobileTableFooter;
